import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "./queryKeys";

export const useQueryInvalidation = () => {
  const queryClient = useQueryClient();

  return {
    invalidateUserData: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.user.all });
    },

    invalidateUserMe: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.user.me() });
    },

    invalidateUserPermission: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.user.permission() });
      queryClient.invalidateQueries({
        queryKey: queryKeys.masterBuyerBoss.withBuyerCode(),
      });
    },

    invalidateGroup: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.group.list() });
    },

    invalidateGroupResourceActionMapping: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.group.groupResourceActionMapping(),
      });
    },

    invalidateMaterialTypesList: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.materialTypes.list()],
        exact: false,
        refetchType: "active",
      });

      queryClient.invalidateQueries({
        queryKey: [queryKeys.materialTypes.listAllSpec()],
      });
    },

    invalidateMaterialTypeSpecs: (materialTypeId: number) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.materialTypes.specs(materialTypeId)],
        exact: false,
        refetchType: "active",
      });

      queryClient.invalidateQueries({
        queryKey: [queryKeys.materialTypes.listAllSpec()],
      });
    },

    invalidateOEMProcesses: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.masterProcess.getOEMProcesses()],
      });
    },

    invalidateOEMPartProcesses: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.masterProcess.getOEMPartProcesses()],
      });
    },

    invalidateOEMPartOtherProcesses: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.masterProcess.getOEMPartOtherProcesses()],
      });
    },

    invalidateSpareProcesses: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.masterProcess.getSpareProcesses()],
      });
    },

    invalidateSparePartProcesses: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.masterProcess.getSparePartProcesses()],
      });
    },

    invalidateSparePartOtherProcesses: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.masterProcess.getSparePartOtherProcesses()],
      });
    },

    invalidateMaterialOtherProcesses: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.masterProcess.getMaterialOtherProcesses()],
      });
    },

    invalidateMaterial2ndTierProcesses: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.masterProcess.getMaterial2ndTierProcesses()],
      });
    },

    invalidateChildPartProcesses: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.masterProcess.getChildPartProcesses()],
      });
    },

    invalidateMaterialSuppliers: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.materialSupplier.all, "list"],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.materialSupplier.all, "detail"],
      });
    },

    invalidateSupplier2nd: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.masterProcess.getSupplier2nd()],
      });
    },

    invalidateReturnScrap: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.masterProcess.all, "returnScrap", "list"],
      });
    },

    invalidateReturnScrapPrice: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.masterProcess.all, "returnScrapPrice", "list"],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.breakdown.all, "childPartV1"],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.breakdown.all, "childPartV2"],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.firstTierSupplier.all, "partsBySupplier"],
      });
    },

    invalidateReasonPriceChange: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.masterProcess.getReasonPriceChange()],
      });
    },

    invalidateMaterialCodes: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.materialSupplier.all, "materialCode"],
      });
    },

    invalidateOtherCostProcesses: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.materialSupplier.all, "otherCostProcesses"],
      });
    },

    invalidateBasePrices: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.basePrice.all],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.breakdown.all, "childPartV1"],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.breakdown.all, "childPartV2"],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.firstTierSupplier.all, "partsBySupplier"],
      });
    },

    invalidateFirstTierSuppliers: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.firstTierSupplier.list(),
      });
    },

    invalidatePart: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.firstTierSupplier.all, "list"],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.firstTierSupplier.all, "partsBySupplier"],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.firstTierSupplier.all, "partSKPBySupplier"],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.flaggedPart.all],
      });
    },

    invalidateChildPartV2: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.breakdown.all, "childPartV2"],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.firstTierSupplier.all, "partsBySupplier"],
      });
    },

    invalidateBreakdownProcess: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.breakdown.all, "breakdownProcessV2"],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.breakdown.all, "childPartV2"],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.breakdown.all, "partProcessV2"],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.breakdown.all, "partOtherProcessV2"],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.firstTierSupplier.all, "partsBySupplier"],
      });
    },

    invalidateImportPart: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.firstTierSupplier.all, "list"],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.firstTierSupplier.all, "partsBySupplier"],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.firstTierSupplier.all, "partSKPBySupplier"],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.breakdown.all, "breakdownProcessV2"],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.breakdown.all, "childPartV2"],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.breakdown.all, "partProcessV2"],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.breakdown.all, "partOtherProcessV2"],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.flaggedPart.all],
      });
    },

    invalidateImportPartV1: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.firstTierSupplier.all, "list"],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.firstTierSupplier.all, "partsBySupplier"],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.firstTierSupplier.all, "partSKPBySupplier"],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.breakdown.all, "childPartV1"],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.breakdown.all, "breakdownProcessV1"],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.flaggedPart.all],
      });
    },

    invalidateBreakdownExchangeRates: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.breakdownExchangeRate.all, "parts"],
      });
    },

    invalidateBreakdownExchangeRateProcess: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.breakdownExchangeRate.all, "parts"],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.breakdownExchangeRate.all, "process"],
      });
    },

    invalidateExchangeRates: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.exchangeRate.all, "list"],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.breakdownExchangeRate.all, "master"],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.breakdown.all, "childPartV1"],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.breakdown.all, "childPartV2"],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.firstTierSupplier.all, "partsBySupplier"],
      });
    },

    invalidateExchangeRateSuppliers: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.exchangeRate.all, "list"],
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.exchangeRate.suppliers(),
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.breakdownExchangeRate.all, "master"],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.breakdown.all, "childPartV1"],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.breakdown.all, "childPartV2"],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.firstTierSupplier.all, "partsBySupplier"],
      });
    },

    invalidateExchangeRateCurrencies: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.exchangeRate.all, "list"],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.exchangeRate.all, "currencies"],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.breakdownExchangeRate.all, "master"],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.breakdown.all, "childPartV1"],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.breakdown.all, "childPartV2"],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.firstTierSupplier.all, "partsBySupplier"],
      });
    },

    invalidateLogActivities: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.logActivity.list(),
      });
    },

    invalidatePAData: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.pa.all });
    },

    invalidateAll: () => {
      queryClient.invalidateQueries();
    },

    invalidateBuyerBoss: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.masterBuyerBoss.withBuyerCode(),
      });
    },

    invalidateManualMasterPart: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.manualMasterPart.all,
      });
    },
  };
};
